import ManropeBoldUrl from 'assets/Manrope-Bold.ttf'
import ManropeRegularUrl from 'assets/Manrope-Regular.ttf'
import { AuthedAxiosRequests } from 'features/auth'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/kestrelApi'
import { jsPDF } from 'jspdf'
import { G1N5, KVAnnotation, KVPhoto, WorldPosition } from 'types'

/**
 * Convert annotations to PDF with enhanced photo integration.
 *
 * When photos are provided, the function will:
 * 1. Score photos for each annotation's position using photo scoring utilities
 * 2. Select the highest scoring (most relevant) photo for each annotation
 * 3. Render the photo with a crosshair overlay at the annotation position
 * 4. Include the rendered image in the PDF export
 *
 * @param annotations - Array of KV annotations to export
 * @param metadata - Optional metadata (component name, site name, organization)
 * @param photos - Array of KV photos for photo scoring (optional)
 * @returns Promise resolving to PDF blob
 */
export async function convertAnnotationsToPDF(
  annotations: KVAnnotation[] | undefined,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  },
  photos?: KVPhoto[],
  modelId?: string,
  authorizedAxios?: AuthedAxiosRequests,
  kestrelApiUrl?: string
): Promise<Blob> {
  const decimalPlaces = 3

  if (!annotations || annotations.length === 0) {
    return await generateEmptyAnnotationsPDF(metadata)
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)
  generateAnnotationsSummary(pdf, annotations)

  // Start annotations on a new page
  pdf.addPage()
  await generateBlackBarAndLogo(pdf)

  await generateAnnotationsContent(
    pdf,
    annotations,
    decimalPlaces,
    photos,
    modelId,
    authorizedAxios,
    kestrelApiUrl
  )
  generatePDFFooter(pdf)

  return pdf.output('blob')
}

/**
 * Export G1N5 inspection data and annotations to PDF with enhanced photo integration.
 *
 * This function generates a comprehensive PDF report that includes:
 * 1. G1N5 inspection data on the front page (area visible, corrosion info, etc.)
 * 2. Annotations with photo integration (when photos are provided)
 * 3. Enhanced formatting with logos and metadata
 *
 * @param g1n5Data - G1N5 inspection data to include on front page
 * @param annotations - Array of KV annotations to export
 * @param metadata - Optional metadata (component name, site name, organization)
 * @param photos - Array of KV photos for photo scoring (optional)
 * @returns Promise resolving to PDF blob
 */
export async function exportG1N5PDF(
  g1n5Data: G1N5,
  annotations: KVAnnotation[] | undefined,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  },
  photos?: KVPhoto[]
): Promise<Blob> {
  const decimalPlaces = 3

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)

  // Add G1N5 data section
  const g1n5EndY = generateG1N5Section(pdf, g1n5Data)

  // Add annotations summary if annotations exist
  if (annotations && annotations.length > 0) {
    generateAnnotationsSummary(pdf, annotations, g1n5EndY + 10)

    // Start annotations on a new page
    pdf.addPage()
    await generateBlackBarAndLogo(pdf)

    await generateAnnotationsContent(pdf, annotations, decimalPlaces, photos)
  }

  generatePDFFooter(pdf)

  return pdf.output('blob')
}

function convertSvgToPngDataUrl(size: number = 48): Promise<string> {
  return new Promise((resolve, reject) => {
    // SVG with white fill for the logo
    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 295.11 295.11" width="${size}" height="${size}">
        <g>
          <path fill="white" d="m139.88,105.79c-12.03,0-20.66,8.4-20.66,19.75s8.85,19.75,20.66,19.75,20.88-8.4,20.88-19.75-9.08-19.75-20.88-19.75Z"/>
          <path fill="white" d="m0,0v295.11h295.11V0H0Zm210.62,89.44h-13.99c-5.45,0-9.76,2.72-10.9,7.04,5.68,8.17,9.08,17.93,9.08,29.06,0,29.28-23.61,51.08-54.94,51.08-5.9,0-11.58-.68-17.03-2.27-3.4,2.27-5.22,5.45-5.22,8.85,0,5.22,4.31,8.63,10.44,8.63h36.09c26,0,46.45,19.15,46.45,44.49h-36.24c-.91-7.49-7.26-12.49-16.57-12.49h-36.09c-22.47,0-37.23-12.94-37.23-32.23,0-12.26,6.13-24.06,15.89-29.96-9.53-9.08-15.21-21.79-15.21-36.09,0-29.28,23.38-51.08,54.71-51.08,8.85,0,17.03,1.82,24.29,4.77,3.41-12.49,14.98-20.43,30.87-20.43h15.58v30.65Z"/>
        </g>
      </svg>
    `

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = size
    canvas.height = size

    const img = new Image()
    const svgBlob = new Blob([svgContent], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      if (ctx) {
        ctx.drawImage(img, 0, 0, size, size)
        const dataUrl = canvas.toDataURL('image/png', 1.0)
        URL.revokeObjectURL(url)
        resolve(dataUrl)
      } else {
        reject(new Error('Failed to get canvas context'))
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG image'))
    }

    img.src = url
  })
}

async function generateBlackBarAndLogo(pdf: jsPDF): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const blackBarHeight = 20

  // Draw black bar at the top
  pdf.setFillColor(0, 0, 0)
  pdf.rect(0, 0, pageWidth, blackBarHeight, 'F')

  // Add Gecko logo
  try {
    const logoDataUrl = await convertSvgToPngDataUrl(144) // Higher resolution
    const logoSize = 12 // Size in mm
    pdf.addImage(logoDataUrl, 'PNG', 10, 4, logoSize, logoSize)
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error)
  }

  // Add Gecko text next to logo
  pdf.setTextColor(255, 255, 255)
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('GECKO ROBOTICS', 25, 12)

  // Add "Defect Report" to the right side of the black bar
  const defectReportText = 'Defect Report'
  const defectReportWidth = pdf.getTextWidth(defectReportText)
  pdf.text(defectReportText, pageWidth - defectReportWidth - 10, 12)

  // Reset text color for subsequent content
  pdf.setTextColor(0, 0, 0)
}

async function generatePDFHeader(
  pdf: jsPDF,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  }
): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const timestamp = new Date().toLocaleString()

  // Add black bar and logo at the top
  await generateBlackBarAndLogo(pdf)

  // Title
  pdf.setFontSize(20)
  pdf.setFont('Manrope', 'bold')
  const date = new Date().toLocaleDateString()
  const componentName = metadata?.componentName || 'Unknown Component'
  const title = `${date}: ${componentName} Defect Report`
  const titleWidth = pdf.getTextWidth(title)
  const titleX = (pageWidth - titleWidth) / 2
  pdf.text(title, titleX, 35)

  // Metadata section
  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')
  let yPosition = 45

  if (metadata?.organizationName) {
    pdf.text(`Organization: ${metadata.organizationName}`, 20, yPosition)
    yPosition += 5
  }

  if (metadata?.siteName) {
    pdf.text(`Site: ${metadata.siteName}`, 20, yPosition)
    yPosition += 5
  }

  if (metadata?.componentName) {
    pdf.text(`Component: ${metadata.componentName}`, 20, yPosition)
    yPosition += 5
  }

  pdf.text(`Generated: ${timestamp}`, 20, yPosition)
  yPosition += 5
  pdf.text('Source: Cantilever', 20, yPosition)

  // Add a line separator
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition + 5, pageWidth - 20, yPosition + 5)
}

function generateAnnotationsSummary(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  startY?: number
): void {
  const pageWidth = pdf.internal.pageSize.getWidth()
  let yPosition = startY || 90

  // Summary section
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('Summary', 20, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')
  const count = annotations.length
  const countText = `Total Defects: ${count}`
  pdf.text(countText, 20, yPosition)
  yPosition += 10

  // Add a line separator
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition, pageWidth - 20, yPosition)
}

async function generateAnnotationsContent(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  decimalPlaces: number,
  photos?: KVPhoto[],
  modelId?: string,
  authorizedAxios?: AuthedAxiosRequests,
  kestrelApiUrl?: string
): Promise<void> {
  let yPosition = 35
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const lineHeight = 6
  const sectionSpacing = 12
  const imageMaxWidth = 120 // mm
  const imageMaxHeight = 90 // mm

  for (let index = 0; index < annotations.length; index++) {
    const annotation = annotations[index]

    // Find best photo for this annotation if data is available
    let bestPhoto = null
    if (photos && modelId && authorizedAxios && kestrelApiUrl) {
      try {
        bestPhoto = await findBestPhotoForAnnotation(
          annotation,
          photos,
          modelId,
          authorizedAxios,
          kestrelApiUrl
        )
      } catch (error) {
        console.warn(
          `Failed to find best photo for annotation ${index + 1}:`,
          error
        )
      }
    }

    // Calculate space needed for this annotation (including potential image)
    const estimatedTextHeight = 80 // Rough estimate for text content
    const spaceNeeded =
      estimatedTextHeight +
      (bestPhoto ? imageMaxHeight + 10 : 0) +
      sectionSpacing

    // Check if we need a new page
    if (yPosition + spaceNeeded > pageHeight - 20) {
      pdf.addPage()
      await generateBlackBarAndLogo(pdf)
      yPosition = 35
    }

    // Annotation header
    pdf.setFontSize(14)
    pdf.setFont('Manrope', 'bold')
    const annotationTitle = `Defect ${index + 1}`
    pdf.text(annotationTitle, 20, yPosition)
    yPosition += lineHeight + 2

    // Annotation details
    pdf.setFontSize(10)
    pdf.setFont('Manrope', 'normal')

    if (annotation.label) {
      const labelText = `Label: ${annotation.label}`
      const labelLines = splitTextToFitWidth(pdf, labelText, pageWidth - 50)
      labelLines.forEach((line) => {
        pdf.text(line, 25, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.notes) {
      pdf.text('Notes:', 25, yPosition)
      yPosition += lineHeight
      const notesLines = splitTextToFitWidth(
        pdf,
        annotation.notes,
        pageWidth - 50
      )
      notesLines.forEach((line) => {
        pdf.text(line, 30, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.position) {
      const positionText = formatPositionText(
        annotation.position,
        decimalPlaces
      )
      pdf.text(`Position: ${positionText}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.severity) {
      pdf.text(`Severity: ${annotation.severity}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.type) {
      pdf.text(`Type: ${annotation.type}`, 25, yPosition)
      yPosition += lineHeight
    }

    // Add photo with crosshair if available
    if (bestPhoto) {
      yPosition += 5 // Small gap before image

      try {
        // Use full size image for better quality, fallback to thumbnail
        const imageUrl =
          (await bestPhoto.photo.getFullSizeUrl()) ||
          (await bestPhoto.photo.getThumbnailUrl())
        if (imageUrl) {
          const imageDataUrl = await renderImageWithCrosshair(
            imageUrl,
            bestPhoto.crosshairX,
            bestPhoto.crosshairY,
            // Remove artificial resolution limits to preserve original quality
            undefined, // maxWidth - let it use original size up to reasonable limit
            undefined // maxHeight - let it use original size up to reasonable limit
          )

          pdf.addImage(
            imageDataUrl,
            'PNG',
            25,
            yPosition,
            imageMaxWidth,
            imageMaxHeight,
            undefined, // alias
            'MEDIUM'
          )
          yPosition += imageMaxHeight + 5

          // Add image caption
          pdf.setFontSize(8)
          pdf.setFont('Manrope', 'normal')
          pdf.text(`Photo: ${bestPhoto.photo.filename}`, 25, yPosition)
          yPosition += 4
        }
      } catch (error) {
        console.warn(
          `Failed to render image for annotation ${index + 1}:`,
          error
        )
        // Add a note that image failed to load
        pdf.setFontSize(8)
        pdf.setFont('Manrope', 'normal')
        pdf.text('(Image could not be loaded)', 25, yPosition)
        yPosition += lineHeight
      }
    }

    // Add spacing between annotations
    yPosition += sectionSpacing
  }
}

function generatePDFFooter(pdf: jsPDF): void {
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageCount = pdf.getNumberOfPages()

  // Add footer to each page
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)

    // Add black line above footer
    pdf.setDrawColor(0, 0, 0) // Black color
    pdf.setLineWidth(0.5)
    pdf.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20)

    pdf.setFontSize(8)
    pdf.setFont('Manrope', 'normal')

    // Page number
    const pageText = `Page ${i} of ${pageCount}`
    const pageTextWidth = pdf.getTextWidth(pageText)
    pdf.text(pageText, pageWidth - pageTextWidth - 20, pageHeight - 10)

    // Company info
    pdf.text('Generated by Cantilever - Gecko Robotics', 20, pageHeight - 10)
  }
}

async function generateEmptyAnnotationsPDF(metadata?: {
  componentName?: string
  siteName?: string
  organizationName?: string
}): Promise<Blob> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)

  // Empty content message
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'normal')
  pdf.text('No annotations available for export.', 20, 90)

  generatePDFFooter(pdf)

  return pdf.output('blob')
}

function formatPositionText(position: string, decimalPlaces: number): string {
  try {
    // Parse position string format "X=-2.512 Y=213.841 Z=252.967"
    const xMatch = position.match(/X=([-\d.]+)/)
    const yMatch = position.match(/Y=([-\d.]+)/)
    const zMatch = position.match(/Z=([-\d.]+)/)

    if (xMatch && yMatch && zMatch) {
      const x = parseFloat(xMatch[1]).toFixed(decimalPlaces)
      const y = parseFloat(yMatch[1]).toFixed(decimalPlaces)
      const z = parseFloat(zMatch[1]).toFixed(decimalPlaces)
      return `X=${x}, Y=${y}, Z=${z}`
    }
  } catch (error) {
    console.warn('Failed to parse position string:', position, error)
  }
  return position
}

function splitTextToFitWidth(
  pdf: jsPDF,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  words.forEach((word) => {
    const testLine = currentLine + (currentLine ? ' ' : '') + word
    const testWidth = pdf.getTextWidth(testLine)

    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  })

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

function parseAnnotationPosition(position: string): number[] | null {
  try {
    // Parse position string format "X=-2.512 Y=213.841 Z=252.967"
    const xMatch = position.match(/X=([-\d.]+)/)
    const yMatch = position.match(/Y=([-\d.]+)/)
    const zMatch = position.match(/Z=([-\d.]+)/)

    if (xMatch && yMatch && zMatch) {
      return [
        parseFloat(xMatch[1]),
        parseFloat(yMatch[1]),
        parseFloat(zMatch[1]),
      ]
    }
  } catch (error) {
    console.warn('Failed to parse annotation position:', position, error)
  }
  return null
}

async function findBestPhotoForAnnotation(
  annotation: KVAnnotation,
  photos: KVPhoto[],
  modelId: string,
  authorizedAxios: AuthedAxiosRequests,
  kestrelApiUrl: string
): Promise<{
  photo: KVPhoto
  crosshairX: number
  crosshairY: number
} | null> {
  if (!annotation.position) return null

  const worldPosition = parseAnnotationPosition(annotation.position)
  if (!worldPosition) return null

  try {
    const position: WorldPosition = {
      x: worldPosition[0],
      y: worldPosition[1],
      z: worldPosition[2],
    }
    const photoResponses = await fetchNearbyPhotos(
      modelId,
      position,
      authorizedAxios,
      kestrelApiUrl
    )

    if (photoResponses.length === 0) return null

    const bestPhotoResponse = photoResponses[0]
    const kvPhoto = photos.find(
      (photo) => photo.filename === bestPhotoResponse.image_name
    )

    if (!kvPhoto) return null

    return {
      photo: kvPhoto,
      crosshairX: bestPhotoResponse.projection.x,
      crosshairY: bestPhotoResponse.projection.y,
    }
  } catch (error) {
    console.error('Failed to find best photo for annotation:', error)
    return null
  }
}

export async function findBestAnnotationImageFilename(
  annotation: KVAnnotation,
  modelId?: string,
  authorizedAxios?: AuthedAxiosRequests,
  kestrelApiUrl?: string
): Promise<string | null> {
  if (!annotation.position) return null

  const worldPosition = parseAnnotationPosition(annotation.position)
  if (!worldPosition) return null

  if (modelId && authorizedAxios && kestrelApiUrl) {
    try {
      const position: WorldPosition = {
        x: worldPosition[0],
        y: worldPosition[1],
        z: worldPosition[2],
      }
      const photoResponses = await fetchNearbyPhotos(
        modelId,
        position,
        authorizedAxios,
        kestrelApiUrl
      )

      if (photoResponses.length === 0) return null

      return photoResponses[0].image_name
    } catch (error) {
      console.error('Failed to find best annotation image filename:', error)
      return null
    }
  }

  return null
}

async function renderImageWithCrosshair(
  imageUrl: string,
  crosshairX: number,
  crosshairY: number,
  maxWidth?: number,
  maxHeight?: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      // Use original image dimensions if no max limits provided,
      // but cap at reasonable limits for PDF performance
      const defaultMaxWidth = 3840 // 4K width
      const defaultMaxHeight = 2160 // 4K height

      const effectiveMaxWidth = maxWidth || Math.min(img.width, defaultMaxWidth)
      const effectiveMaxHeight =
        maxHeight || Math.min(img.height, defaultMaxHeight)

      // Calculate scaled dimensions while maintaining aspect ratio
      const aspectRatio = img.width / img.height
      let canvasWidth = effectiveMaxWidth
      let canvasHeight = effectiveMaxWidth / aspectRatio

      if (canvasHeight > effectiveMaxHeight) {
        canvasHeight = effectiveMaxHeight
        canvasWidth = effectiveMaxHeight * aspectRatio
      }

      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Draw the image scaled to fit
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)

      // Calculate crosshair position on the scaled canvas
      // The crosshair coordinates are in the original image space
      const scaleX = canvasWidth / img.width
      const scaleY = canvasHeight / img.height
      const scaledCrosshairX = crosshairX * scaleX
      const scaledCrosshairY = crosshairY * scaleY

      // Ensure crosshair is within canvas bounds
      const clampedX = Math.max(0, Math.min(scaledCrosshairX, canvasWidth))
      const clampedY = Math.max(0, Math.min(scaledCrosshairY, canvasHeight))

      // Draw crosshair with size scaled to resolution
      const crosshairSize = Math.max(
        32, // Minimum size
        Math.min(canvasWidth, canvasHeight) * 0.05 // 5% of smallest dimension
      )
      drawCrosshair(ctx, clampedX, clampedY, crosshairSize)

      resolve(canvas.toDataURL('image/png', 1.0)) // Use maximum quality PNG
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = imageUrl
  })
}

function drawCrosshair(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number = 24
): void {
  const halfSize = size / 2
  const circleRadius = halfSize * 1.1 // A bit bigger than before
  const lineWidth = Math.max(3, size * 0.1) // Scale line width with size

  ctx.save()

  // Draw simple red circle border
  ctx.strokeStyle = 'rgba(255, 0, 0, 1.0)' // Red
  ctx.lineWidth = lineWidth
  ctx.beginPath()
  ctx.arc(x, y, circleRadius, 0, 2 * Math.PI)
  ctx.stroke()

  ctx.restore()
}

async function addManropeFont(pdf: jsPDF): Promise<void> {
  const [regularBase64, boldBase64] = await Promise.all([
    convertTtfToBase64(ManropeRegularUrl),
    convertTtfToBase64(ManropeBoldUrl),
  ])

  pdf.addFileToVFS('Manrope-Regular.ttf', regularBase64)
  pdf.addFont('Manrope-Regular.ttf', 'Manrope', 'normal')
  pdf.addFileToVFS('Manrope-Bold.ttf', boldBase64)
  pdf.addFont('Manrope-Bold.ttf', 'Manrope', 'bold')
}

async function convertTtfToBase64(fontUrl: string): Promise<string> {
  const response = await fetch(fontUrl)
  const arrayBuffer = await response.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  const binaryString = Array.from(bytes, (byte) =>
    String.fromCharCode(byte)
  ).join('')
  return btoa(binaryString)
}

function generateG1N5Section(pdf: jsPDF, g1n5Data: G1N5): number {
  const pageWidth = pdf.internal.pageSize.getWidth()
  let yPosition = 90

  // G1N5 section header
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('G1N5 Inspection Data', 20, yPosition)
  yPosition += 10

  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')

  // Two-column layout for G1N5 data
  const leftColumnX = 20
  const rightColumnX = pageWidth / 2 + 10
  const lineHeight = 6

  // Left column
  let leftYPosition = yPosition

  // Area Visible
  if (g1n5Data.areaVisible !== null) {
    const areaText = `Area Visible: ${g1n5Data.areaVisible}%`
    pdf.text(areaText, leftColumnX, leftYPosition)
    leftYPosition += lineHeight
  }

  // Total Area
  if (g1n5Data.totalArea !== null) {
    const totalAreaText = `Total Area of Zone: ${g1n5Data.totalArea}`
    pdf.text(totalAreaText, leftColumnX, leftYPosition)
    leftYPosition += lineHeight
  }

  // Pictures Taken
  if (g1n5Data.picturesTaken !== null) {
    const picturesText = `Pictures Taken: ${g1n5Data.picturesTaken}`
    pdf.text(picturesText, leftColumnX, leftYPosition)
    leftYPosition += lineHeight
  }

  // Level 2 Inspection Required
  const level2Text = `Level 2 Inspection Required: ${g1n5Data.level2InspectionRequired ? 'Yes' : 'No'}`
  pdf.text(level2Text, leftColumnX, leftYPosition)
  leftYPosition += lineHeight

  // Right column
  let rightYPosition = yPosition

  // Corrosion/Coating Failure
  if (g1n5Data.corrosionCoatingFailure !== null) {
    const corrosionText = `Corrosion/Coating Failure: ${g1n5Data.corrosionCoatingFailure}%`
    pdf.text(corrosionText, rightColumnX, rightYPosition)
    rightYPosition += lineHeight
  }

  // Corrosion Type
  if (g1n5Data.corrosionType) {
    const typeText = `Corrosion Type: ${g1n5Data.corrosionType}`
    pdf.text(typeText, rightColumnX, rightYPosition)
    rightYPosition += lineHeight
  }

  // Coating Condition
  if (g1n5Data.coatingCondition) {
    const conditionText = `Coating Condition: ${g1n5Data.coatingCondition}`
    pdf.text(conditionText, rightColumnX, rightYPosition)
    rightYPosition += lineHeight
  }

  // Update yPosition to the lower of the two columns
  yPosition = Math.max(leftYPosition, rightYPosition) + 5

  // Picture File Names (full width)
  if (g1n5Data.pictureFileNames) {
    pdf.text('Picture File Names:', leftColumnX, yPosition)
    yPosition += lineHeight
    const fileNameLines = splitTextToFitWidth(
      pdf,
      g1n5Data.pictureFileNames,
      pageWidth - 50
    )
    fileNameLines.forEach((line) => {
      pdf.text(line, leftColumnX + 5, yPosition)
      yPosition += lineHeight
    })
    yPosition += 3
  }

  // Comments (full width)
  if (g1n5Data.comments) {
    pdf.text('Comments:', leftColumnX, yPosition)
    yPosition += lineHeight
    const commentLines = splitTextToFitWidth(
      pdf,
      g1n5Data.comments,
      pageWidth - 50
    )
    commentLines.forEach((line) => {
      pdf.text(line, leftColumnX + 5, yPosition)
      yPosition += lineHeight
    })
  }

  yPosition += 10

  // Add a line separator
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition, pageWidth - 20, yPosition)

  return yPosition + 5
}
